import clsx from "clsx";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { Button } from "@shared/ui/button";
import { Icon } from "@shared/ui/icon";
import { useScroll } from "@shared/ui/scroll/scroll";
import { SharedSocial } from "@shared/ui/social-shared";
import { Body } from "@shared/ui/typography";
import { processText } from "@shared/utils/string";

import s from "./article-navigation.module.scss";

export type NavigationProps = ComponentProps<"div"> & {
  sections: string[];
  currentSection: string;
  onOpenNavigation: () => void;
};

export const Navigation = (props: NavigationProps) => {
  const { sections, currentSection, onOpenNavigation } = props;

  const scrollbarRef = useRef<HTMLDivElement | null>(null);

  const scroll = useScroll();

  const sectionIndex = useMemo(() => {
    return sections.findIndex(
      (item) => `section-${processText(item)}` === currentSection,
    );
  }, [sections, currentSection]);

  useEffect(() => {
    scrollbarRef?.current?.style?.setProperty(
      "--position",
      sectionIndex > 0 ? sectionIndex.toString() : "0",
    );
  }, [sectionIndex]);

  const handleClickOnNavButton = useCallback(
    (id: string) => {
      scroll?.scrollTo(`#section-${processText(id)}`, { offset: -100 });
    },
    [scroll],
  );

  return (
    <div className={clsx(s.root)}>
      <Button
        className={s.mobileButton}
        data-inner-element="mobileButton"
        onClick={onOpenNavigation}
      >
        <span>{sections[sectionIndex]}</span>
        <Icon name="arrow-down" size={24} />
      </Button>
      <ol className={s.sections} data-inner-element="sections">
        <Button className={s.cross} onClick={onOpenNavigation}>
          <Icon name="cross" size={24} />
        </Button>
        <div className={s.scrollbar} ref={scrollbarRef} />
        <div className={s.scrollbarRail} />
        {sections.map((section) => (
          <Body
            tag="li"
            size="caption"
            weight="semiBold"
            key={section}
            className={s.link}
          >
            <Button onClick={() => handleClickOnNavButton(section)}>
              {section}
            </Button>
          </Body>
        ))}
      </ol>
      <SharedSocial className={s.shared} />
    </div>
  );
};

Navigation.displayName = "Navigation";
